import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from '../../../utils/api';
import { fmtDateTime } from '../../../utils/region';
import OverflowChips, { OChip } from '../../OverflowChips';
import Pagination, { getPageSize } from '../../Pagination';
import type {
  AwBankLine, AwClaimDoors, AwExpenseCategories, AwReimbursement, AwReimbursementBody, AwReimbursementDetail, AwReimbursementFilter, AwReimbursementLineBody, AwReimbursementPage,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, baht, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Foot, Loader, Stat, actBtn, cancelBtn, chip, chipOn, emptyLine, field, isoDate, lbl, r2, todayIso, useDialogClose } from '../dialog';
import { DocEntriesBtn } from '../docEntries';
import { RecordEvidence } from '../attachments';   // owner 2026-11-02: the claim's supporting files and linked records
import { NoCell, SmallLine, StatusCell } from '../listCells';   // the same deliberate cycle as Purchases.tsx's: every cross-reference is inside a body
import NewDocBtn from '../NewDocBtn';
import RowMenu, { type RowMenuEntry } from '../RowMenu';

// ── Expenses › Expense categories (owner 2026-10-13) ────────────────────────────────────────────
// The "categories" are the client company's own expense accounts (chart_of_accounts.class =
// 'expense'): the statutory ledger knows no finer grouping and this pane invents none. Every figure
// is the P&L's own rule — Σ(dr − cr) over POSTED lines of the period, opening/closing entries
// excluded — computed on the server per account and per month (/acct-workspace/expense-categories);
// the client only lays it out. The bar is a visual aid scaled to the largest account and carries no
// figure of its own. Accounts with nothing posted are listed on purpose and read muted: an
// accountant checks that an account exists and is unused, not only what has been posted to it.

// Section labels are the chart's own (ChartOfAccounts.tsx), so both screens name a section alike.
const CF_KEY: Record<string, string> = {
  '': 'pa_chart_cf_none', operating: 'pa_chart_cf_operating', investing: 'pa_chart_cf_investing',
  financing: 'pa_chart_cf_financing', cash: 'pa_chart_cf_cash', noncash: 'pa_chart_cf_noncash',
};
// cit_deductible is the chart's own tag ('' unknown | yes | no | limited) — shown as stored, never inferred.
const CIT: Record<string, { key: string; style: React.CSSProperties }> = {
  yes: { key: 'aw_cat_cit_yes', style: pillGreen },
  no: { key: 'aw_cat_cit_no', style: pillRed },
  limited: { key: 'aw_cat_cit_limited', style: pillAmber },
};
const pillAccent: React.CSSProperties = { ...pill, color: 'var(--accent-text)', background: 'var(--accent-bg)', border: '1px solid var(--accent)' };
const track: React.CSSProperties = { height: 6, borderRadius: 3, background: 'var(--bg-app)', overflow: 'hidden', minWidth: 90 };
const fill: React.CSSProperties = { height: '100%', borderRadius: 3, background: 'var(--accent)' };
const ymLabel = (ym: string) => { const [y, m] = String(ym || '').split('-'); return y && m ? `${m}/${y}` : ym; };

export function ExpenseCategoriesPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const loc = useLocation();
  const [params] = useSearchParams();
  const [data, setData] = useState<AwExpenseCategories | null>(null);
  const [err, setErr] = useState('');
  const [byMonth, setByMonth] = useState(false);

  useEffect(() => {
    let alive = true;
    setErr('');
    api.awExpenseCategories(ctx.clientId, ctx.periodId)
      .then((d: AwExpenseCategories) => { if (alive) setData(d); })
      .catch((e: any) => { if (alive) { setData(null); setErr(String(e?.message || 'error')); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.periodId, ctx.reload]);

  // Same page, other submenu: setNav lives in the shell and is not in ctx, so the link rebuilds the
  // current URL with menu/sub swapped — ?client= and anything an embedded child wrote stay intact.
  const chartTo = useMemo(() => {
    const n = new URLSearchParams(params); n.set('menu', 'accounting'); n.set('sub', 'chart');
    return { pathname: loc.pathname, search: `?${n.toString()}` };
  }, [params, loc.pathname]);

  if (err) return <div style={errBox}>{err}</div>;
  if (!data) return <div style={{ padding: 26, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" /></div>;

  const rows = data.rows || [];
  const months = data.months || [];
  const maxAbs = rows.reduce((m, r) => Math.max(m, Math.abs(Number(r.amount) || 0)), 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
          {dmy(data.period.start_date)} – {dmy(data.period.end_date)} · {t('aw_cat_total')}: <b style={{ color: 'var(--text-primary)' }}>{baht(data.total)}</b>
        </div>
        <div style={{ flex: 1 }} />
        {rows.length > 0 && months.length > 0 && (
          <button onClick={() => setByMonth(v => !v)} aria-pressed={byMonth}
            style={{ ...btn, ...(byMonth ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>
            <i className="ti ti-calendar" aria-hidden /> {t('aw_cat_by_month')}
          </button>
        )}
      </div>

      {!rows.length ? (
        <div style={card}>
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{t('aw_cat_empty')}</div>
          <Link to={chartTo} style={{ marginTop: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            {t('aw_cat_empty_link')} <i className="ti ti-arrow-right" style={{ fontSize: 12 }} aria-hidden />
          </Link>
        </div>
      ) : (
        // The month columns scroll inside the card; the page itself never scrolls sideways.
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table style={{ ...table, minWidth: byMonth ? 760 + months.length * 112 : undefined }}>
            <thead>
              <tr>
                <th style={th}>{t('aw_cat_code')}</th>
                <th style={th}>{t('aw_cat_name')}</th>
                <th style={th}>{t('aw_cat_cf_section')}</th>
                <th style={th}>{t('aw_cat_cit')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_cat_amount')}</th>
                <th style={{ ...th, width: 120 }} aria-hidden />
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_cat_lines')}</th>
                {byMonth && months.map(ym => <th key={ym} style={{ ...th, textAlign: 'right' }}>{ymLabel(ym)}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const amount = Number(r.amount) || 0;
                const zero = amount === 0;
                const pct = maxAbs ? Math.round(Math.abs(amount) / maxAbs * 100) : 0;
                const cit = CIT[String(r.cit_deductible || '')];
                const tone = zero ? 'var(--text-tertiary)' : 'var(--text-primary)';
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, color: tone, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{r.code}</td>
                    <td style={{ ...td, color: tone }}>
                      <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {r.name_th}
                        {/* Deactivated in the chart yet still carrying posted lines of this period: the server keeps it so
                            the rows add up to the P&L, and tags it so it is not read as a live category. */}
                        {Number(r.active) === 0 && <span style={pillGrey}>{t('aw_cat_inactive')}</span>}
                      </div>
                      {r.name_en && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{r.name_en}</div>}
                    </td>
                    <td style={td}><span style={r.cf_section ? pillAccent : pillGrey}>{t(CF_KEY[r.cf_section] || 'pa_chart_cf_none')}</span></td>
                    <td style={td}>{cit ? <span style={cit.style}>{t(cit.key)}</span> : <span style={pillGrey}>{t('aw_cat_cit_unknown')}</span>}</td>
                    <td style={{ ...num, color: tone, fontWeight: zero ? 400 : 700 }}>{baht(amount)}</td>
                    <td style={{ ...td, width: 120 }}><div style={track}><div style={{ ...fill, width: `${pct}%` }} /></div></td>
                    <td style={{ ...num, color: tone }}>{r.lines}</td>
                    {byMonth && months.map(ym => {
                      const v = Number(r.months?.[ym]) || 0;
                      return <td key={ym} style={{ ...num, color: v ? tone : 'var(--text-tertiary)' }}>{v ? baht(v) : '–'}</td>;
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div style={note}>{t('aw_cat_note')}</div>
    </div>
  );
}

// ── Payroll › Reimbursements (owner 2026-10-13, batch 4; under Expenses until batch 10 R3) ────────
// The last grey submenu. A REGISTER of the client company (merchant_reimbursements + lines, read and
// written through /api/acct-workspace/reimbursements): employees' expense claims, submitted → approved
// or rejected → paid, with a receipt per line. Nothing here is the firm's and nothing posts to the
// statutory ledger (manual entries stay its only feed); paying a claim "from" a statement line only
// MARKS that line (the B2 rule), the same door as matching it in Banking. The ONLY arithmetic on this
// side is the register's own rule, shown live so the operator submits what the server will store:
// claim total = Σ lines at 2 dp. Status, the summary and the line count come from the server, never
// recomputed here. The claim is sent as multipart because its receipts ride with it; every other
// verb is JSON.

type TF = (key: string, opts?: Record<string, unknown>) => string;
const RB_FILTERS: AwReimbursementFilter[] = ['all', 'submitted', 'approved', 'rejected', 'paid'];
const MAX_STR = 200;
const MAX_DESC = 500;
const MAX_FILE = 10 * 1024 * 1024;   // the route's own cap (SPEC-B4 §2), checked here so the refusal is read before the upload
// The route's accepted receipt types — the SAME list as services/merchantReimbursements.ts RECEIPT_ACCEPT
// (HEIF included), so nothing the route would store is refused here first. The extension is checked
// beside the MIME because a browser hands HEIC/HEIF over with an empty type more often than not.
const FILE_TYPES = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const FILE_EXT = /\.(pdf|jpe?g|png|webp|heic|heif)$/i;
const FILE_ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.heic,.heif,application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif';
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
// Amber = waiting on someone, blue = approved and owed, green = settled, red = refused — the Purchases pills' reading.
const rbPill = (s: string): React.CSSProperties => s === 'paid' ? pillGreen : s === 'approved' ? pillBlue : s === 'rejected' ? pillRed : pillAmber;
const when = (v: number) => v ? fmtDateTime(v) : '';
const fin = (s: string): number | null => { if (String(s).trim() === '') return null; const n = Number(s); return Number.isFinite(n) ? n : NaN; };
const okStr = (s: string, max = MAX_STR) => s.length <= max;
/** The server's currency rule (readCurrency): exactly three letters, or blank for the company's default. */
const curOk = (c: string) => !c.trim() || /^[A-Za-z]{3}$/.test(c.trim());
const hasFile = (v: boolean | number | undefined) => !!Number(v) || v === true;
/** The route's error turned into a sentence; a 409 names the state the register is in. */
const rerr = (e: any, t: TF): string => {
  const msg = String(e?.message || '');
  if (e?.status === 409) return `${t('aw_rb_err_conflict')}${msg ? ` — ${msg}` : ''}`;
  if (e?.status === 404) return t('aw_pay_err_not_found');
  return msg || t('aw_bank_err_generic');
};
const Field = ({ label, children, span }: { label: string; children: React.ReactNode; span?: number }) => (
  <div style={span ? { gridColumn: `span ${span}` } : undefined}><label style={lbl}>{label}</label>{children}</div>
);
/** Every page of a list the server pages (limit 200 = its largest page), so the picker never stops
 *  silently at the newest 200 rows (the Purchases pane's rule). The page cap is a safety stop only. */
async function loadAllPages<T>(fetchPage: (page: number) => Promise<{ rows: T[]; total: number }>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 50; page++) {
    const d = await fetchPage(page);
    const rows = d.rows || [];
    out.push(...rows);
    if (!rows.length || out.length >= (Number(d.total) || 0)) break;
  }
  return out;
}
/** The unmatched money-out lines of the company's statements, every page (the API pages by state only). */
async function loadUnmatchedDebitLines(clientId: string): Promise<AwBankLine[]> {
  const rows = await loadAllPages<AwBankLine>(page => api.awBankLines(clientId, { state: 'unmatched', page, limit: 200 }));
  return rows.filter(l => Number(l.debit) > 0);
}
const isSubmitted = (c: AwReimbursement) => c.status === 'submitted';
const canDelete = (c: AwReimbursement) => c.status === 'submitted' || c.status === 'rejected';   // the server's own rule, mirrored so the button is only offered where it would succeed
// ⛔ The two read-only seats (a client's own books login, the auditor) are offered nothing that writes —
// no New claim, no Approve / Reject / Pay / Unpay / Edit / Delete, no Unmatch — in the list or the claim's window.
// middleware.ts refuses every non-GET from a books login with a JSON 403, and a refused call pages the
// admins; the auditor never reaches /api/acct-workspace at all. Batch 10 R3 made Reimbursements the books
// seat's one Payroll entry (menus.ts), so it reads the claims here: rows, window and receipts stay open to
// it (design §3.6 "同今天（可见）") — Documents.tsx's rule for a read-only login.
const readsOnly = (ctx: AwCtx) => ctx.me.role === 'books' || ctx.me.role === 'auditor';

// ── Claim dialog (new · edit while submitted) with the lines editor ─────────────────────────────
// `id` = the server's line id on an edit, sent back with the line so the server keeps THAT line's receipt
// whatever was corrected on it (date, description, amount — writeClaimLines matches by id first). A line
// without one is new to the server; a fresh claim's lines never carry one.
type LineDraft = { id?: string; line_date: string; category: string; description: string; amount: string; file: File | null; filename: string };
const blankLine = (date: string): LineDraft => ({ line_date: date, category: '', description: '', amount: '', file: null, filename: '' });
type CategoryOption = { value: string; label: string };
function ClaimDialog({ ctx, detail, onClose, onSaved }: { ctx: AwCtx; detail?: AwReimbursementDetail; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const listId = useId();
  const claim = detail?.claim;
  const edit = !!claim;
  const [claimant, setClaimant] = useState(claim?.claimant_name || '');
  const [ref, setRef] = useState(claim?.claimant_ref || '');
  const [claimDate, setClaimDate] = useState(claim?.claim_date || todayIso());
  // Blank = the company's own default currency, applied by the server (readCurrency → currencyCode).
  const [currency, setCurrency] = useState(claim?.currency || '');
  const [noteTxt, setNoteTxt] = useState(claim?.note || '');
  const [lines, setLines] = useState<LineDraft[]>(detail?.lines?.length
    ? detail.lines.map(l => ({ id: l.id, line_date: l.line_date, category: l.category || '', description: l.description, amount: String(l.amount), file: null, filename: hasFile(l.has_file) ? (l.filename || t('aw_rb_receipt')) : '' }))
    : [blankLine(todayIso())]);
  const [cats, setCats] = useState<CategoryOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12. The flag the shell's close gate reads (dialog.tsx), in the shape of the house precedent
  // (Admin/JobRequiredDocs.tsx): the EDIT PATH sets it and nothing else does. ⛔ Never a diff
  // against the values the form opened with — an edit opens pre-filled with the claim and its
  // lines, and a diff would call that dirty before the operator had touched a key, which teaches
  // them to click the warning away. The category list arriving from the server is not an edit either.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  // The category suggestions are the company's own expense accounts (chart_of_accounts.class = 'expense',
  // active ones) — offered through a datalist, so the field stays free text as the register defines it.
  // A chart that cannot be read leaves the list empty; the form still works.
  useEffect(() => {
    let alive = true;
    api.acctChart(ctx.clientId).then((d: any) => {
      if (!alive) return;
      const seen = new Set<string>();
      const out: CategoryOption[] = [];
      for (const a of (d?.accounts || []) as any[]) {
        if (String(a.class) !== 'expense' || Number(a.active) === 0) continue;
        const value = String(a.name_th || a.name_en || '').trim();
        if (!value || seen.has(value)) continue;
        seen.add(value);
        out.push({ value, label: [a.code, a.name_en && a.name_en !== value ? a.name_en : ''].filter(Boolean).join(' · ') });
      }
      setCats(out);
    }).catch(() => { if (alive) setCats([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const amounts = lines.map(l => { const n = fin(l.amount); return n === null || Number.isNaN(n) ? null : n; });
  // The register's own rule: total = Σ lines at 2 dp — shown so the operator submits what the server stores.
  const total = amounts.every(a => a !== null) ? r2(amounts.reduce((s, a) => s + (a as number), 0)) : null;
  const fileProblem = (l: LineDraft, i: number): string => {
    if (!l.file) return '';
    const typeOk = FILE_TYPES.includes(l.file.type) || FILE_EXT.test(l.file.name);
    if (!typeOk) return t('aw_rb_file_bad_type', { n: i + 1 });
    if (l.file.size > MAX_FILE) return t('aw_rb_file_too_big', { n: i + 1 });
    return '';
  };
  const fileProblems = lines.map(fileProblem).filter(Boolean);
  const lineOk = (l: LineDraft, i: number) => { const n = fin(l.amount); return isoDate(l.line_date) && !!l.description.trim() && okStr(l.description, MAX_DESC) && okStr(l.category) && n !== null && !Number.isNaN(n) && n > 0 && !fileProblem(l, i); };
  const curBad = !curOk(currency);
  const ready = !!claimant.trim() && okStr(claimant) && okStr(ref) && isoDate(claimDate) && !curBad && lines.length >= 1 && lines.every(lineOk);
  // Every line field — date, category, description, amount, receipt — goes through here, so one
  // mark covers the whole editor and no column can be added that forgets to set it.
  const setLine = (i: number, patch: Partial<LineDraft>) => { setDirty(true); setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l)); };
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const body: AwReimbursementBody = {
      claimant_name: claimant.trim(), claimant_ref: ref.trim(), claim_date: claimDate, note: noteTxt.trim(),
      // On an edit every kept line names its id: the server keeps a receipt by id before anything else, so a
      // corrected figure or a fixed typo never costs the line its receipt (reviewer 2026-10-13 — without the id
      // the server saw a NEW line and unlinked the old one's bytes). Never on create: a fresh claim has no line to keep.
      lines: lines.map((l): AwReimbursementLineBody => ({ ...(edit && l.id ? { id: l.id } : {}), line_date: l.line_date, category: l.category.trim(), description: l.description.trim(), amount: r2(Number(l.amount)) })),
    };
    if (currency.trim()) body.currency = currency.trim().toUpperCase();
    try {
      if (claim) {
        // (Review 2026-11-03) A line with a receipt dropped is asked again here, with the date this save gives the claim and each new date it gives a line that keeps its receipt: a month may have been filed since this window opened, and a refused write pages the admins.
        if ((detail?.lines || []).some(o => hasFile(o.has_file) && !lines.some(l => l.id === o.id))) {
          const moved = lines.filter(l => (detail?.lines || []).some(o => hasFile(o.has_file) && o.id === l.id && o.line_date !== l.line_date)).map(l => l.line_date);
          for (const date of Array.from(new Set([claimDate, ...moved]))) {
            const now: AwReimbursementDetail = await api.awReimbursement(ctx.clientId, claim.id, date);
            if (now.filesHeld) { setErr(t(now.filesHeld.code === 'not_yours' ? 'aw_rb_files_not_yours' : 'aw_rb_files_held')); setBusy(false); return; }
          }
        }
        await api.awReimbursementUpdate(ctx.clientId, claim.id, body);
      } else await api.awReimbursementCreate(ctx.clientId, body, lines.map(l => l.file));
      onSaved();
    } catch (e: any) { setErr(rerr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={claim ? t('aw_rb_edit_title') : t('aw_rb_new_title')} docNo={claim?.claim_no} onClose={onClose} busy={busy} dirty={dirty} fullscreen
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={claim ? 'ti ti-device-floppy' : 'ti ti-send'} label={claim ? t('aw_doc_save') : t('aw_rb_create_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1.4fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_rb_claimant')}><input value={claimant} onChange={e => ed(setClaimant, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_rb_claimant_ref')}><input value={ref} onChange={e => ed(setRef, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_rb_claim_date')}><input type="date" value={claimDate} onChange={e => ed(setClaimDate, e.target.value)} style={field} /></Field>
          <Field label={t('aw_col_currency')}>
            <input value={currency} onChange={e => ed(setCurrency, e.target.value)} maxLength={3} placeholder={t('aw_pay_currency_default')} style={field} />
            <div style={{ ...note, marginTop: 3, color: curBad ? '#B42318' : undefined }}>{t('aw_pay_currency_hint')}</div>
          </Field>
        </div>
        <div>
          <label style={lbl}>{t('aw_rb_lines')}</label>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }}>#</th>
                  <th style={{ ...th, width: 140 }}>{t('aw_rb_line_date')}</th>
                  <th style={{ ...th, width: 180 }}>{t('aw_rb_category')}</th>
                  <th style={th}>{t('aw_rb_description')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>{t('aw_bank_amount')}</th>
                  <th style={{ ...th, width: 200 }}>{t('aw_rb_receipt')}</th>
                  <th style={{ ...th, width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                    <td style={td}><input type="date" value={l.line_date} onChange={e => setLine(i, { line_date: e.target.value })} style={field} /></td>
                    <td style={td}><input list={listId} value={l.category} onChange={e => setLine(i, { category: e.target.value })} maxLength={MAX_STR} style={field} /></td>
                    <td style={td}><input value={l.description} onChange={e => setLine(i, { description: e.target.value })} maxLength={MAX_DESC} style={field} /></td>
                    <td style={td}><input type="number" step="0.01" min="0" value={l.amount} onChange={e => setLine(i, { amount: e.target.value })} style={{ ...field, textAlign: 'right' }} /></td>
                    <td style={td}>
                      {/* Receipts travel with the multipart POST only: the edit is JSON (SPEC-B4 §2), so an existing claim shows what it has. */}
                      {edit
                        ? <span style={{ fontSize: 12, color: l.filename ? 'var(--text-secondary)' : 'var(--text-tertiary)' }}>{l.filename ? <><i className="ti ti-paperclip" aria-hidden /> {l.filename}</> : t('aw_rb_no_file')}</span>
                        : <input type="file" accept={FILE_ACCEPT} onChange={e => setLine(i, { file: e.target.files?.[0] || null })} style={{ fontSize: 11.5, maxWidth: 190 }} />}
                    </td>
                    <td style={td}>
                      {/* (Review 2026-11-03) Dropping a line takes its receipt off the claim, which a closed period, a filed month or somebody else's receipt forbids: the server refuses it, and a refusal pages the admins, so it is not offered. */}
                      {!(detail?.filesHeld && l.id && l.filename) && <button onClick={() => { setDirty(true); setLines(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : [blankLine(claimDate)]); }} title={t('aw_rb_remove_line')} style={{ ...actBtn, padding: '4px 7px', color: '#B42318' }}><i className="ti ti-x" aria-hidden /></button>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ ...td, borderBottom: 'none' }}>
                    <button onClick={() => { setDirty(true); setLines(ls => [...ls, blankLine(ls[ls.length - 1]?.line_date || claimDate)]); }} style={actBtn}><i className="ti ti-plus" aria-hidden /> {t('aw_rb_add_line')}</button>
                  </td>
                  <td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{total === null ? '—' : amt(total)}</td>
                  <td colSpan={2} style={{ ...td, borderBottom: 'none' }} />
                </tr>
              </tfoot>
            </table>
          </div>
          <datalist id={listId}>{cats.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</datalist>
          <div style={note}>{t('aw_rb_lines_rule')} {t('aw_rb_category_hint')}</div>
          <div style={note}>{edit ? t('aw_rb_edit_files_hint') : t('aw_rb_file_hint')}</div>
          {edit && !!detail?.filesHeld && <div data-claim-files-held={detail.filesHeld.code} style={{ ...note, color: '#92400E' }}><i className="ti ti-lock" aria-hidden /> {t(detail.filesHeld.code === 'not_yours' ? 'aw_rb_files_not_yours' : 'aw_rb_files_held')}</div>}
          {fileProblems.map((m, i) => <div key={i} style={{ fontSize: 11.5, color: '#B42318' }}>{m}</div>)}
        </div>
        <Field label={t('aw_bank_note_opt')}><textarea value={noteTxt} onChange={e => ed(setNoteTxt, e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></Field>
        {!!claim && <RecordEvidence clientId={ctx.clientId} kind="reimbursement" id={claim.id} />}
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Pay dialog ──────────────────────────────────────────────────────────────────────────────────
// A claim is paid whole: the route takes no amount, and a statement line chosen here must equal the
// claim's total and sit in an account of the claim's currency (400 otherwise — payClaim). So the picker
// offers ONLY what the server would accept (SPEC-B4 §4: "unmatched debit lines with the same amount"):
// no greyed, unchoosable options (owner 2026-10-13: nothing grey). A one-line count says how many other
// unmatched money-out lines exist, so the operator knows they are there without being shown what
// cannot be sent.
function PayDialog({ ctx, claim, onClose, onDone }: { ctx: AwCtx; claim: AwReimbursement; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [paidDate, setPaidDate] = useState(todayIso());
  const [paidRef, setPaidRef] = useState('');
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState<AwBankLine[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — see ClaimDialog. The date opens on today and the bank lines arrive from the server:
  // neither is an edit, so opening this and closing it asks nothing.
  const [dirty, setDirty] = useState(false);
  const ed = <T,>(set: (v: T) => void, v: T) => { setDirty(true); set(v); };
  useEffect(() => {
    let alive = true;
    loadUnmatchedDebitLines(ctx.clientId).then(rows => { if (alive) setLines(rows); }).catch(() => { if (alive) setLines([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const total = r2(Number(claim.total_amount));
  const cur = String(claim.currency || '').toUpperCase();
  // The choosable lines: equal amount, and the account's currency equal to the claim's when both say one
  // (the server's own tests in payClaim, mirrored so nothing offered is refused); newest first.
  const exact = useMemo(() => (lines || [])
    .filter(l => r2(Number(l.debit)) === total && (!l.account_currency || !cur || String(l.account_currency).toUpperCase() === cur))
    .sort((x, y) => String(y.txn_date).localeCompare(String(x.txn_date))), [lines, total, cur]);
  const others = (lines || []).length - exact.length;
  const pickLine = (id: string) => {
    setDirty(true);   // choosing the line this claim was paid from is the decision, not a side effect
    setLineId(id);
    const l = (lines || []).find(x => x.id === id);
    if (l) setPaidDate(l.txn_date);   // the payment takes the line's date; the field stays editable
  };
  const ready = isoDate(paidDate) && okStr(paidRef);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awReimbursementPay(ctx.clientId, claim.id, { paid_date: paidDate, paid_ref: paidRef.trim() || undefined, bank_line_id: lineId || undefined });
      onDone();
    } catch (e: any) { setErr(rerr(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_rb_pay_title')} docNo={claim.claim_no} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-cash" label={t('aw_rb_pay_record_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 760 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ fontWeight: 700 }}>{claim.claim_no}</span><span style={{ flex: '1 1 160px' }}>{claim.claimant_name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{t('aw_col_total')}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(claim.total_amount)} {claim.currency}</b></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
          <Field label={t('aw_rb_paid_date')}><input type="date" value={paidDate} onChange={e => ed(setPaidDate, e.target.value)} style={field} /></Field>
          <Field label={t('aw_rb_paid_ref')}><input value={paidRef} onChange={e => ed(setPaidRef, e.target.value)} maxLength={MAX_STR} style={field} /></Field>
        </div>
        <div>
          <label style={lbl}>{t('aw_rb_from_line')}</label>
          {lines === null ? <Loader /> : !exact.length ? (
            <div style={{ ...note, marginTop: 2 }}><i className="ti ti-info-circle" aria-hidden /> {t('aw_rb_from_line_no_exact')}</div>
          ) : (
            <select value={lineId} onChange={e => pickLine(e.target.value)} style={field}>
              <option value="">{t('aw_rb_from_line_none')}</option>
              {exact.map(l => (
                <option key={l.id} value={l.id}>
                  {dmy(l.txn_date)} · {l.account_number} · {l.description} · {amt(l.debit)} · {t('aw_bank_exact')}
                </option>
              ))}
            </select>
          )}
          <div style={note}>{t('aw_rb_from_line_hint')}{others > 0 ? ` ${t('aw_rb_from_line_others', { n: others })}` : ''}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

// ── Reject dialog (the reason is required by the route) ─────────────────────────────────────────
function RejectDialog({ ctx, claim, onClose, onDone }: { ctx: AwCtx; claim: AwReimbursement; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // FS-12 — a written refusal is the work; losing it to a stray backdrop click is what this stops.
  const [dirty, setDirty] = useState(false);
  const ready = !!reason.trim() && okStr(reason);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await api.awReimbursementReject(ctx.clientId, claim.id, reason.trim()); onDone(); }
    catch (e: any) { setErr(rerr(e, t)); setBusy(false); }
  };
  return (
    <Dialog fullscreen title={t('aw_rb_reject_title')} docNo={claim.claim_no} onClose={onClose} busy={busy} dirty={dirty}
      footer={<Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-ban" label={t('aw_rb_reject_btn')} t={t} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxWidth: 720 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}><b style={{ color: 'var(--text-primary)' }}>{claim.claim_no}</b> · {claim.claimant_name} · {amt(claim.total_amount)} {claim.currency}</div>
        <div>
          <label style={lbl}>{t('aw_rb_reject_reason')}</label>
          <textarea value={reason} onChange={e => { setDirty(true); setReason(e.target.value); }} rows={3} maxLength={MAX_STR} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{t('aw_rb_reject_hint')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
      </div>
    </Dialog>
  );
}

/** The status-driven actions of the claim's window, what the list's Status dropdown and ⋯ offer too (ReimbursementsPane), so all of them offer
 *  exactly what the server would accept: submitted → approve / reject / edit / delete; approved → pay; paid → unpay; rejected → delete. */
function ClaimActions({ c, busy, onApprove, onReject, onPay, onUnpay, onEdit, onDelete }: {
  c: AwReimbursement; busy: string; onApprove: () => void; onReject: () => void; onPay: () => void; onUnpay: () => void; onEdit: () => void; onDelete: () => void;
}) {
  const { t } = useTranslation();
  const spin = (icon: string) => busy === c.id ? 'ti ti-loader' : icon;
  const primary: React.CSSProperties = { ...btnPrimary, padding: '5px 11px', fontSize: 12 };
  return (
    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
      {c.status === 'submitted' && <button onClick={onApprove} disabled={!!busy} style={primary}><i className={spin('ti ti-check')} aria-hidden /> {t('aw_rb_approve')}</button>}
      {c.status === 'submitted' && <button onClick={onReject} disabled={!!busy} style={actBtn}><i className="ti ti-ban" aria-hidden /> {t('aw_rb_reject_btn')}</button>}
      {c.status === 'approved' && <button onClick={onPay} disabled={!!busy} style={primary}><i className="ti ti-cash" aria-hidden /> {t('aw_rb_pay_btn')}</button>}
      {c.status === 'paid' && <button onClick={onUnpay} disabled={!!busy} style={actBtn}><i className={spin('ti ti-arrow-back-up')} aria-hidden /> {t('aw_rb_unpay')}</button>}
      {isSubmitted(c) && <button onClick={onEdit} disabled={!!busy} style={actBtn}><i className={spin('ti ti-pencil')} aria-hidden /> {t('aw_profile_edit')}</button>}
      {canDelete(c) && <button onClick={onDelete} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className={spin('ti ti-trash')} aria-hidden /> {t('aw_bank_delete')}</button>}
    </span>
  );
}

/** A window's Close, through the shell's gate (the X, Esc and the backdrop run the same one). The house's copy — the same
 *  component as Banking's, Purchases' and Petty Cash's (testDirtyClose holds the four equal). ⛔ A COMPONENT: the gate
 *  arrives by context from INSIDE the Dialog's tree, which a hook in the component rendering the Dialog cannot see. */
const GateCancel = ({ onClose, busy, label }: { onClose: () => void; busy?: boolean; label: string }) => {
  const close = useDialogClose(onClose);
  return <button onClick={close} disabled={busy} style={cancelBtn}>{label}</button>;
};

// ── A claim, full screen (FS-2, batch 12 tail T2; TL-11) ─────────────────────────────────────────────────────────────
/**
 * The claim's own window — what the right-hand Drawer showed, now the workspace's one full-screen shell (Esc closes it;
 * the Drawer answered no Esc at all). It follows the expense record window (EXP-16): the claim's facts, its lines with
 * their receipts (each opened in a new tab, TL-12), and the statement line that paid it — read-only, so it keeps no
 * `dirty` and asks nothing on closing. "Read-only" means NO INPUTS, never fewer actions: for a writing seat the footer
 * carries every action the Drawer offered, unchanged — Approve, Reject…, Pay…, Unpay, Edit…, Delete (ClaimActions; Unpay
 * and Delete keep their native confirm) — and the bank line keeps its Unmatch. Edit, Pay and Reject open their own
 * full-screen windows over this one (the pane renders them after it, so they are the later registration and Esc closes
 * the top one only). A read-only seat (a client's books login, the auditor) gets Close and Entries only.
 */
export function ClaimWindow({ ctx, id, onClose, onEdit, onPay, onReject }: {
  ctx: AwCtx; id: string; onClose: () => void;
  /** The list's ways to its editor and its two doors. Absent where the window was reached from another record (a linked record, owner 2026-11-02), which reads it and offers none of them. */
  onEdit?: (d: AwReimbursementDetail) => void; onPay?: (c: AwReimbursement) => void; onReject?: (c: AwReimbursement) => void;
}) {
  const { t } = useTranslation();
  const readOnly = readsOnly(ctx) || !onEdit || !onPay || !onReject;
  const [d, setD] = useState<AwReimbursementDetail | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.awReimbursement(ctx.clientId, id).then((x: AwReimbursementDetail) => { if (alive) { setD(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(rerr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Every write re-reads through ctx.bump — the workspace's one signal — so the list behind the window moves too.
  const act = async (key: string, fn: () => Promise<unknown>, close = false) => {
    if (busy) return; setBusy(key); setErr('');
    try { await fn(); ctx.bump(); if (close) onClose(); } catch (e: any) { setErr(rerr(e, t)); } finally { setBusy(''); }
  };
  // The receipt is fetched with the login token and opened as a blob (a bare href on /api/… has no Authorization header).
  const openFile = async (lid: string) => {
    if (busy || !d) return; setBusy(lid); setErr('');
    try { openBlob(await api.awReimbursementLineFileBlobUrl(ctx.clientId, d.claim.id, lid)); } catch (e: any) { setErr(rerr(e, t)); } finally { setBusy(''); }
  };
  const c = d?.claim;
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
  return (
    <Dialog fullscreen title={t('aw_rb_detail_title')} docNo={c ? `${c.claim_no} · ${c.claimant_name}` : undefined} onClose={onClose}
      footer={(
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }} data-claim-foot>
          {/* Batch 11 R8a: a claim has no posting rule yet (design §7) — the panel says so and points at a manual voucher. */}
          {c && <DocEntriesBtn ctx={ctx} kind="reimbursement" id={c.id} docNo={c.claim_no} />}
          <div style={{ flex: 1 }} />
          {!readOnly && c && (
            <ClaimActions c={c} busy={busy}
              onApprove={() => act(c.id, () => api.awReimbursementApprove(ctx.clientId, c.id))}
              onReject={() => onReject?.(c)} onPay={() => onPay?.(c)}
              onUnpay={() => { if (window.confirm(t('aw_rb_unpay_confirm'))) act(c.id, () => api.awReimbursementUnpay(ctx.clientId, c.id)); }}
              onEdit={() => onEdit?.(d as AwReimbursementDetail)}
              onDelete={() => { if (window.confirm(t('aw_rb_delete_confirm', { no: c.claim_no }))) act(c.id, () => api.awReimbursementDelete(ctx.clientId, c.id), true); }} />
          )}
          <GateCancel onClose={onClose} label={t('aw_bank_close')} />
        </div>
      )}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }} data-claim-window={id}>
        {err && <div style={errBox}>{err}</div>}
        {!d || !c ? (!err && <Loader />) : (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={rbPill(c.status)} data-claim-status={c.status}>{t(`aw_rb_status_${c.status}`)}</span>
            </div>
            <div style={{ ...card, maxWidth: 760 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
                <Row k={t('aw_rb_claim_date')} v={dmy(c.claim_date)} />
                <Row k={t('aw_col_total')} v={`${amt(c.total_amount)} ${c.currency}`} />
                <Row k={t('aw_rb_claimant_ref')} v={c.claimant_ref || '—'} />
                <Row k={t('aw_rb_lines')} v={d.lines.length} />
                {(c.status === 'approved' || c.status === 'rejected' || c.status === 'paid') && c.decided_by_name && (
                  <Row k={t('aw_rb_decided_by')} v={<>{c.decided_by_name}{c.decided_at ? <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}> · {when(c.decided_at)}</span> : null}</>} />
                )}
                {c.status === 'paid' && <Row k={t('aw_rb_paid')} v={<>{dmy(c.paid_date)}{c.paid_ref ? <span style={{ fontWeight: 400, color: 'var(--text-tertiary)' }}> · {c.paid_ref}</span> : null}</>} />}
              </div>
              {c.status === 'rejected' && c.reject_reason && <div style={{ fontSize: 12.5, marginTop: 8, color: '#B42318' }}><b>{t('aw_rb_reject_reason')}:</b> {c.reject_reason}</div>}
              {c.note && <div style={{ fontSize: 12.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{c.note}</div>}
              <div style={{ ...note, marginTop: 8 }}>{c.created_by_name && <span>{c.created_by_name} · {when(c.created_at)}</span>}</div>
            </div>
            <div>
              <div style={sectionHead}>{t('aw_rb_lines')}</div>
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={table}>
                  <thead><tr><th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('aw_rb_line_date')}</th><th style={th}>{t('aw_rb_category')}</th><th style={th}>{t('aw_rb_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th><th style={th}>{t('aw_rb_receipt')}</th></tr></thead>
                  <tbody>
                    {d.lines.map(l => (
                      <tr key={l.id}>
                        <td style={{ ...td, color: 'var(--text-tertiary)' }}>{l.line_no}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(l.line_date)}</td>
                        <td style={td}>{l.category || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}</td>
                        <td style={td}>{l.description}</td>
                        <td style={num}>{amt(l.amount)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {hasFile(l.has_file)
                            ? <button onClick={() => openFile(l.id)} disabled={!!busy} style={actBtn} title={l.filename || undefined}><i className={busy === l.id ? 'ti ti-loader' : 'ti ti-file-search'} aria-hidden /> {t('aw_rb_open_file')}</button>
                            : <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t('aw_rb_no_file')}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={4} style={{ ...num, borderBottom: 'none', fontWeight: 700 }}>{t('aw_col_total')}</td><td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{amt(c.total_amount)}</td><td style={{ ...td, borderBottom: 'none' }} /></tr></tfoot>
                </table>
              </div>
            </div>
            <div>
              <div style={sectionHead}>{t('aw_rb_bank_line')}</div>
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                {!d.bank_line ? <div style={emptyLine}>{t('aw_rb_no_bank_line')}</div> : (
                  <table style={table}>
                    <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_bank_account')}</th><th style={th}>{t('aw_bank_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_debit')}</th>{!readOnly && <th style={th}>{t('aw_col_actions')}</th>}</tr></thead>
                    <tbody>
                      <tr>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(d.bank_line.txn_date)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{d.bank_line.account_number}</td>
                        <td style={td}>{d.bank_line.description}{d.bank_line.details && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{d.bank_line.details}</div>}</td>
                        <td style={num}>{amt(d.bank_line.debit)}</td>
                        {!readOnly && (
                          <td style={{ ...td, whiteSpace: 'nowrap' }}>
                            {/* Unmatching the line is the pay route in reverse (SPEC-B4 §2: unmatch = unpay) — one door both ways. */}
                            {c.status === 'paid' && (
                              <button onClick={() => { if (window.confirm(t('aw_rb_unpay_confirm'))) act(c.id, () => api.awReimbursementUnpay(ctx.clientId, c.id)); }} disabled={!!busy} style={actBtn} data-claim-unmatch>
                                <i className={busy === c.id ? 'ti ti-loader' : 'ti ti-link-off'} aria-hidden /> {t('aw_bank_unmatch_btn')}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <RecordEvidence clientId={ctx.clientId} kind="reimbursement" id={c.id} />
            <div style={note}>{t('aw_rb_register_note')}</div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Payroll › Reimbursements — the claims register ──────────────────────────────────────────────
export function ReimbursementsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const readOnly = readsOnly(ctx);
  const [status, setStatus] = useState<AwReimbursementFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwReimbursementPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [detailId, setDetailId] = useState('');
  const [dlg, setDlg] = useState<{ kind: 'new' } | { kind: 'edit'; detail: AwReimbursementDetail } | { kind: 'pay'; c: AwReimbursement } | { kind: 'reject'; c: AwReimbursement } | null>(null);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awReimbursements(ctx.clientId, { status, q: qd, from, to, page, limit })
      .then((d: AwReimbursementPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit, summary: { byCurrency: [] } }); setErr(rerr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, status, qd, from, to, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(rerr(e, t)); } finally { setBusy(''); }
  };
  // Edit needs the lines, which the list does not carry: fetch the detail, then open the editor (a read — no re-read signal).
  const edit = async (c: AwReimbursement) => {
    if (busy) return; setBusy(c.id); setErr('');
    try { const d: AwReimbursementDetail = await api.awReimbursement(ctx.clientId, c.id); setDlg({ kind: 'edit', detail: d }); }
    catch (e: any) { setErr(rerr(e, t)); } finally { setBusy(''); }
  };
  const del = (c: AwReimbursement) => { if (window.confirm(t('aw_rb_delete_confirm', { no: c.claim_no }))) act(c.id, () => api.awReimbursementDelete(ctx.clientId, c.id)); };
  const unpay = (c: AwReimbursement) => { if (window.confirm(t('aw_rb_unpay_confirm'))) act(c.id, () => api.awReimbursementUnpay(ctx.clientId, c.id)); };
  const chips: OChip[] = RB_FILTERS.map(s => ({ key: s, label: t(`aw_rb_filter_${s}`), active: s === status, onClick: () => { setStatus(s); setPage(1); } }));
  const rows = data?.rows || [];
  const summary = data?.summary?.byCurrency || [];
  const done = () => { setDlg(null); ctx.bump(); };
  // ── One claim's doors, on its Status dropdown and in its ⋯ (owner 2026-11-02, the seven columns; the requirement's section 1 names every
  // expense-related document, and a claim is one) ─────────────────────────────────────────────────────────────────────────────────────
  // The buttons this column used to hold, with their conditions and their dialogs, as Purchases.tsx's orders have them. The conditions are
  // asked of the claim as it stands when either opens (GET /doors), since a click on a stale one would be a refused 4xx, which pages the admins.
  const readDoors = (c: AwReimbursement) => (): Promise<AwClaimDoors> => api.awRowDoors(ctx.clientId, 'reimbursement', c.id);
  const statusDoors = (c: AwReimbursement, v: AwClaimDoors | null, why: string): RowMenuEntry[] => {
    const st = v?.status || c.status;
    const wait = { waiting: (!v && !why) || !!busy, withheld: why || undefined };
    return [
      ...(st === 'submitted' ? [
        { key: 'approve', icon: 'ti-check', label: t('aw_rb_approve'), ...wait, onPick: () => act(c.id, () => api.awReimbursementApprove(ctx.clientId, c.id)) },
        { key: 'reject', icon: 'ti-ban', label: t('aw_rb_reject_btn'), ...wait, onPick: () => setDlg({ kind: 'reject', c }) },
      ] : []),
      ...(st === 'approved' ? [{ key: 'pay', icon: 'ti-cash', label: t('aw_rb_pay_btn'), ...wait, onPick: () => setDlg({ kind: 'pay', c }) }] : []),
      ...(st === 'paid' ? [{ key: 'unpay', icon: 'ti-arrow-back-up', label: t('aw_rb_unpay'), ...wait, onPick: () => unpay(c) }] : []),
    ];
  };
  const claimMenu = (c: AwReimbursement, v: AwClaimDoors | null, why: string): RowMenuEntry[] => {
    if (v && !v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
    const open: RowMenuEntry = { key: 'open', icon: 'ti-arrows-maximize', label: t('aw_er_open'), onPick: () => setDetailId(c.id) };
    if (readOnly) return [open];
    const now = { ...c, status: v?.status || c.status };
    const wait = { waiting: (!v && !why) || !!busy, withheld: why || undefined };
    return [
      open,
      ...statusDoors(c, v, why),
      ...(isSubmitted(now) ? [{ key: 'edit', icon: 'ti-pencil', label: t('aw_profile_edit'), ...wait, onPick: () => edit(c) }] : []),
      ...(canDelete(now) ? [{ key: 'delete', icon: 'ti-trash', danger: true, label: t('aw_bank_delete'), ...wait, onPick: () => del(c) }] : []),
    ];
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      {/* date range · search (the search box sits on the second row, the lists' house rule) */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_bank_from')} style={inp} />
        <span style={{ color: 'var(--text-tertiary)' }}>–</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_bank_to')} style={inp} />
        {(from || to) && <button onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_bank_clear_dates')} style={{ ...actBtn, padding: '4px 7px' }}><i className="ti ti-x" aria-hidden /></button>}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_rb_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
        <div style={{ flex: 1 }} />
        {/* ➕ New claim, top right as on Sales (owner 2026-11-02, NewDocBtn.tsx; no Export here) — not for a seat that only
            reads (readsOnly, above). */}
        {!readOnly && <NewDocBtn onClick={() => setDlg({ kind: 'new' })} title={t('aw_rb_new_title')} />}
      </div>
      {err && <div style={errBox}>{err}</div>}
      {/* ⛔ Cards per currency, never a total across them (the owner console's rule, kept by /reimbursements.summary). */}
      {summary.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {summary.map(s => (
            <React.Fragment key={s.currency}>
              <Stat label={`${t('aw_rb_sum_submitted')} · ${s.currency}`} value={amt(s.submitted_total)} sub={t('aw_rb_sum_submitted_n', { n: s.submitted_count })} tone={Number(s.submitted_count) ? '#92400E' : undefined} />
              <Stat label={`${t('aw_rb_sum_approved_unpaid')} · ${s.currency}`} value={amt(s.approved_unpaid_total)} tone={Number(s.approved_unpaid_total) ? '#1E40AF' : undefined} />
            </React.Fragment>
          ))}
        </div>
      )}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_rb_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                {/* Seven columns (owner 2026-11-02, "Standardized Document Table Layout"). The line count is the claim's window, which lists them. */}
                <th style={th}>{t('aw_col_no')}</th><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_col_payee')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_total')}</th><th style={th}>{t('aw_col_currency')}</th>
                <th style={th}>{t('aw_col_status')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id}>
                  <NoCell sub={c.claimant_ref || undefined}
                    no={<button onClick={() => setDetailId(c.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5 }}>{c.claim_no}</button>} />
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(c.claim_date)}</td>
                  <td style={td}>{c.claimant_name}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{amt(c.total_amount)}</td>
                  <td style={td}>{c.currency}</td>
                  {/* A rejected claim has no door of its status (its Delete is in the ⋯), so it stays a pill, as does a read-only seat's. */}
                  <StatusCell<AwClaimDoors> word={t(`aw_rb_status_${c.status}`)} tone={rbPill(c.status)} value={c.status}
                    menu={!readOnly && c.status !== 'rejected' ? { read: readDoors(c), busy: busy === c.id, items: (v, why) => {
                      if (why) return [{ key: 'failed', note: why }];
                      if (!v) return [{ key: 'reading', note: t('aw_list_reading') }];
                      if (!v.found) return [{ key: 'gone', note: t('aw_doc_menu_gone') }];
                      const doors = statusDoors(c, v, why);
                      return doors.length ? doors : [{ key: 'none', note: t('aw_list_status_no_door') }];
                    } } : undefined}>
                    {/* The decision's own detail under the pill: the refusal's reason, the payment's date — as stored. */}
                    {c.status === 'rejected' && c.reject_reason && <SmallLine name="reason" tone="#B42318">{c.reject_reason}</SmallLine>}
                    {c.status === 'paid' && c.paid_date && <SmallLine name="paid">{dmy(c.paid_date)}{c.bank_line_id && <span style={{ ...pillBlue, marginLeft: 6 }} title={t('aw_pay_from_line_mark')}><i className="ti ti-link" aria-hidden /> {t('aw_bank_match')}</span>}</SmallLine>}
                  </StatusCell>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <RowMenu<AwClaimDoors> busy={busy === c.id} items={(v, why) => claimMenu(c, v, why)} read={readOnly ? undefined : readDoors(c)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {/* Pager driven by the SERVER's echoed limit, the workspace convention (Documents.tsx). */}
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_rb_register_note')}</div>

      {/* The claim's window first, the doors it opens after it: each opens OVER it (the later registration on the one Esc
          stack, painted on top), and Esc closes the top one only. */}
      {detailId && <ClaimWindow ctx={ctx} id={detailId} onClose={() => setDetailId('')} onEdit={d => setDlg({ kind: 'edit', detail: d })} onPay={c => setDlg({ kind: 'pay', c })} onReject={c => setDlg({ kind: 'reject', c })} />}
      {dlg?.kind === 'new' && <ClaimDialog ctx={ctx} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'edit' && <ClaimDialog ctx={ctx} detail={dlg.detail} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'pay' && <PayDialog ctx={ctx} claim={dlg.c} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === 'reject' && <RejectDialog ctx={ctx} claim={dlg.c} onClose={() => setDlg(null)} onDone={done} />}
    </div>
  );
}
